import type { Article } from './types'

/**
 * Office help: recorded time, per diem, Problems, and the road to Sage.
 *
 * Labels quoted in bold are copied from the screens. When a screen changes,
 * update the article in the same commit.
 */
export const OFFICE_TIME_ARTICLES: Article[] = [
  // ------------------------------------------------------------------ //
  {
    slug: 'office-week-routine',
    title: 'A week in the office',
    section: 'Office: time and pay',
    audience: 'office',
    summary: 'The pay week in order: check Problems, review, correct, apply overtime, settle per diem, then pay.',
    body: `
This is the order that works best for a pay week. Pay periods run **Sunday to Saturday**, so most of this happens early the following week, once supervisors have finished entering.

## 1. Start with Problems

Open [Problems](/office/problems). The number beside **Problems** in the menu is the count of open rows. Clear anything that would make pay wrong before you start checking hours:

- **Field staff on no crew** — those people cannot have time recorded by anyone.
- **Prevailing wage jobs with no rate** — their rates will be guesses.
- **Crew days moved onto a different kind of job** — the work types are probably wrong.

See [Problems](/help/problems) for every check.

## 2. Look at each crew's week

Open [Crew by week](/office/crew-week) and pick the **Week ending** date. Each supervisor's crew is one grid, people down the side and Sunday to Saturday across. Look for missing days, a person with far more or fewer hours than the rest of the crew, and anyone on **2 jobs** or more. Click a day's hours to fix a typo. See [Crew by week](/help/crew-by-week).

If you prefer to check job by job, use [Review by week](/office/review) instead.

## 3. Fix whole crew days

If a day was recorded on the wrong job or the wrong date, open it and use **Move this day**. Don't delete and re-enter it: moving keeps the hours, per diem and the history together. See [A crew day (office view)](/help/submission-detail-office).

## 4. Apply overtime

The field records every hour at straight time. Open the [Labor register](/office/register), tick **Not yet sent to Sage**, and read the red banner at the top. It lists each person and week that looks like overtime with none applied. Re-code the hours as shown in [Applying overtime](/help/applying-overtime).

## 5. Settle per diem

Open the [Per diem register](/office/per-diem-register) for the same week. Tick any days that should not have been claimed and remove them in one go. See [Per diem register](/help/per-diem-register).

## 6. Final read of the register

Back in the [Labor register](/office/register), with **Not yet sent to Sage** ticked, read the week **By employee**. That grouping is what each person will be paid, laid out like Sage Time Track Entry so you can compare the two screens line for line.

## 7. Pay the week in Sage

Today the portal does not send time to Sage on its own; see [Sending time to Sage](/help/sending-time-to-sage). Once Sage has run payroll for the week, the portal treats that week as paid and locks per diem and day moves for it.

> Fix things in the portal **before** Sage pays the week. After that, corrections belong in Sage.
`,
  },

  // ------------------------------------------------------------------ //
  {
    slug: 'submissions',
    title: 'Submissions',
    section: 'Office: time and pay',
    audience: 'office',
    summary: 'The office home screen: the Sage connection check and every crew day recorded.',
    routes: ['/office'],
    body: `
This is the first screen you see in the office side of the portal. It answers two questions: is the portal talking to Sage, and what crew days have been recorded?

## Sage connection

The top card shows which Sage company the portal is reading and a badge:

- **Connected** — everything is normal.
- **Degraded** — the portal cannot read some of Sage right now. Jobs, employees and codes may fail to load until it recovers. Tell Tom.

The **Reference view** table lists the Sage lists the portal reads (jobs, employees, cost codes and so on) and how many **Records** each has. These are read live from Sage every time, so they are never out of date. A count that suddenly drops to zero is worth reporting.

## Crew submissions

One row per crew day a supervisor (or the office) recorded.

| Column | What it means |
|---|---|
| **Date** | The day worked |
| **Job** | The job the crew day was opened on |
| **Submitted by** | Who entered it |
| **Crew** | How many people are on the day |
| **Lines** | How many pay lines (Regular, travel, per diem and so on) |
| **Hours** | Total hours on the day |
| **Status** | Where the day stands |

Most days show **Approved**: the supervisor who records a day also approves it, so there is no separate sign-off. A day that has been posted to Sage would show as posted, and is then locked.

## Where to go next

This list is for a quick look. To check or correct time, use:

- [Crew by week](/office/crew-week) — each crew's whole week across every job.
- [Review by week](/office/review) — one job's week as a grid, with a link into each day.
- [Labor register](/office/register) — every line in the Sage Time Track layout, editable in place.

See [A week in the office](/help/office-week-routine) for the order to do these in.
`,
  },

  // ------------------------------------------------------------------ //
  // TODO(verify): the register does not grey out hours in a week Sage has
  // already paid (only Exported/Posted lines lock). Confirm that is intended
  // before telling Tim the register will stop him.
  {
    slug: 'labor-register',
    title: 'Labor register',
    section: 'Office: time and pay',
    audience: 'office',
    summary: 'Every recorded line in the Sage Time Track Entry layout, with corrections in place.',
    routes: ['/office/register'],
    body: `
The labor register lists every recorded pay line in the same layout as Sage **Time Track Entry**: same columns, cost codes shown as 800-000-000 and employee numbers as 21-F0276. Read it side by side with Sage without translating.

## Groupings and filters

The four buttons along the top change how the lines are grouped and subtotaled. The data is the same in each, so a fix made in one shows in all.

- **By employee** — what each person will be paid. Use this for the final check.
- **By job** — what each job absorbed in labor.
- **Pay period** — everything in the date range, in one flat list.
- **By crew day** — as each day was submitted.

**From** and **To** limit the dates. **Not yet sent to Sage** hides lines that have already gone to Sage; this is the view to work from before payroll, and it also turns on the overtime warning (below). If you see **truncated — narrow the dates**, there are too many lines to show at once; shorten the date range.

## Columns that need explaining

| Column | What it means |
|---|---|
| **Wage** | **Prevailing** for a prevailing wage job (hover to see the job rate and fringe), **Certified** for a certified payroll job |
| **Job Number** | **↳ travelled here** means travel charged to the job the person drove to, not the job the crew day was opened on |
| **Cost Code** / **Description** | The work type, as Sage will see it |
| **Earnings Code**, **Shift**, **Dept** | Sage codes. A blank shift is filled in by Sage at import |
| **Est. Rate** / **Est. Amount** | A guide only. Taken from the employee card (rate 1 or 2 times the earnings code multiplier). Sage works out the real rate |
| **Amount** | Dollar lines such as per diem |
| **Sage** | **NotPosted** until a line goes to Sage |

A rate shown greyed with a **?** is one the employee card is known not to predict, such as prevailing wage jobs and rider rates. Treat **Est. Rate** as a sanity check, not a number to reconcile against.

## Correcting a line

1. Type a reason in **Reason for corrections**. It is optional for the office, but it is saved against every change you make while it is filled in, and it is what answers a paycheck question months later.
2. Click **Edit** on the row. Change the work type in **Cost Code**, or the hours or amount, then **Save**.
3. To add a line for the same person on the same day, click **+ Line**, pick the work type (it starts on **Overtime**), enter hours or an amount and click **Add**.
4. To remove a line that should not exist, click **Delete** and confirm.

Every change is saved to the audit trail, which you can read on the crew day screen under **Correction history**.

## Why can't I edit this row?

A row that says **locked** has already gone to Sage. The portal will not change time Sage already holds, because the two would no longer agree. Make that correction in Sage.

If the portal says the person *already has a line for this pay type and cost code on this day*, correct the existing line instead of adding a second one.

## The overtime warning

With **Not yet sent to Sage** ticked, a red banner appears if any week still needs overtime applied. It lists the first few people and weeks and links to **Review these on Problems →**. See [Applying overtime](/help/applying-overtime).
`,
  },

  // ------------------------------------------------------------------ //
  {
    slug: 'applying-overtime',
    title: 'Applying overtime',
    section: 'Office: time and pay',
    audience: 'office',
    summary: 'Supervisors record straight time only. How the office spots overtime weeks and re-codes the hours.',
    body: `
Supervisors cannot record overtime. Every hour from the field arrives as straight time, and the office decides what is overtime. That keeps the rule in one place, but it means a missed week is paid short. The portal watches for it.

## How a week gets flagged

A person's week is flagged when their hours go over the limits on the [Overtime rules](/office/overtime-rules) screen **and** they have no overtime line that week:

- The weekly limit, normally 40 hours, applies everywhere that has no rule of its own.
- Some states also have a daily limit (and some a double-time limit). A day's hours are added up across every job worked that day. If the day crosses states, the strictest rule wins.
- Travel hours count, because they are hours worked.

Weeks already sent to Sage drop off the check.

## Where you see it

- On [Problems](/office/problems), under **Weeks that look like overtime, with none applied**. Each row names the person and the week ending, and says why, for example "10 h on a AK day (over 8)" or "46 h in the week (over 40)".
- On the [Labor register](/office/register), as a red banner when **Not yet sent to Sage** is ticked.

## Applying it

1. Open the [Labor register](/office/register), choose **By employee**, and set **From** / **To** to the week.
2. Fill in **Reason for corrections**, for example "OT over 40".
3. Find the day where the overtime starts. Click **Edit** on the Regular line, lower the hours to the straight-time part, and **Save**.
4. On the same row click **+ Line**. **Overtime** is already picked. Enter the overtime hours and click **Add**. The new line lands on the same person and the same crew day, so it cannot end up on the wrong date.
5. Repeat for later days in the week as needed.
6. Click **Recheck** on Problems. The row is gone.

> The flag clears as soon as the person has **any** overtime line that week. It does not check that the split is right, so check the totals yourself before moving on.

## Why can't I just mark it fine?

You can. **This is fine** on the Problems row hides it, with your reason saved against your name. Use it for a week that is genuinely exempt, not to skip the work.
`,
  },

  // ------------------------------------------------------------------ //
  {
    slug: 'review-by-week',
    title: 'Review by week',
    section: 'Office: time and pay',
    audience: 'office',
    summary: "One job's week as a grid: people down the side, days across, with each crew day one click away.",
    routes: ['/office/review'],
    body: `
Supervisors submit one day at a time, but a crew usually works a job for five or six days. This screen puts a job's whole week on one grid so you can check it in one look.

## The list of weeks

The screen opens on **Recorded time**: one card per job and week. Each card shows:

- The job number and name, with **Prevailing wage** or **Certified** if it applies.
- Total hours for the week, in the corner.
- The dates, how many days, how many crew, how many lines, and who submitted them.

Click **Review the week** to open it.

## Inside a week

**Hours by person and day** is the grid. Each crew member is a row (with their employee number), each day is a column, and **Total** is on the right. **Day total** runs along the bottom. A dot means nothing on that day. Dollar lines such as per diem show under the hours in smaller type.

Things to look for:

- A person missing from a day the rest of the crew worked.
- One person with noticeably more or fewer hours than everyone else.
- A day that is much longer than usual, which may need overtime.

**Days submitted** lists the crew days behind the grid: **Date**, **Submitted by**, **Crew**, **Lines**, **Hours** and **Status**. Click **Open** to go to that day and correct it. See [A crew day (office view)](/help/submission-detail-office).

Click **← All weeks** to go back to the list.

## Review by week or Crew by week?

- **Review by week** answers "what went into this job?" It is the right view for job cost and for a prevailing wage job.
- [Crew by week](/help/crew-by-week) answers "what did this crew do all week?" It shows people who moved between jobs mid-week, which a single job view cannot.

> Anything already posted to Sage is locked. Those corrections belong in Sage.
`,
  },

  // ------------------------------------------------------------------ //
  {
    slug: 'crew-by-week',
    title: 'Crew by week',
    section: 'Office: time and pay',
    audience: 'office',
    summary: "Each crew's whole pay week across every job: fix hours and add or remove per diem in place.",
    routes: ['/office/crew-week'],
    body: `
This screen, titled **Crews by pay period**, shows each supervisor's crew for one pay week, across every job they worked. It is laid out like the paper sheet: people down the side, **Sun** to **Sat** across.

## Picking the week

Choose a **Week ending** date. Pay periods end on a Saturday. The list marks weeks with **no time entered** and weeks that are **paid**. The bar under the menu stays on screen as you scroll and shows the dates and one of:

- **Open** — Sage has not paid this week yet. You can still make changes.
- **Paid** with the check date — Sage has run payroll for this week.
- **No time entered** — nothing recorded yet.

It also shows the number of crews, people, hours and per diem for the week.

## Reading a crew

Each card is headed by the supervisor's name, with the crew's hours and per diem total and the jobs they worked. In the grid:

- **· 2 jobs** (or more) after a name means the person moved jobs during the week.
- The colored badge is the person's role on the crew: Super, QC or Technician.
- **Hours** and **Per diem** on the right are the person's totals. Per diem shows days and dollars.
- **Crew total** is the bottom row.

## Correcting hours

1. Click the hours in a day. The lines behind that day open underneath, often just one, sometimes Regular plus travel.
2. Type a reason in **Why the change? (optional)**. It is saved with the change.
3. Change the hours and click **Save**. Totals update straight away.

A line with a badge instead of a box is locked. **Already exported to Sage** means fix it in Sage. **Sage has paid this period** means payroll has already run for the week. A supervisor may also see **Week closed to supervisors - ask the office**; you are never held to that. See [Supervisor cutoff](/help/supervisor-cutoff).

To change a work type, add a line or move a whole day, use the [Labor register](/office/register) or the crew day screen.

## Adding or removing per diem

Use this for days with no hours, such as a weekend the crew stayed on site.

1. Click **Per diem** on the crew's card. A tick row appears under each person.
2. Tick a box to add per diem. Untick a ticked box to take it off. Hover a ticked box to see the amount and job.
3. If you are adding days, pick the **Job for the days you are adding**. It is never guessed, because per diem is costed to a job.
4. If you are removing days, you can add a note saying why.
5. Click **Save**. The button says what it will do, for example **Save: add 2 days, remove 1 day**.

The rate comes from the job's location and each person's tier, never typed in. See [Per diem rates](/help/per-diem-rates).

## Why can't I…

- **…see the Per diem button?** Sage has paid this week. Per diem for a paid week has to be handled in Sage.
- **…save per diem?** If anyone already has per diem on a day you ticked, nothing is saved and the message lists who and where. Removals are all or nothing too.
`,
  },

  // ------------------------------------------------------------------ //
  {
    slug: 'submission-detail-office',
    title: 'A crew day (office view)',
    section: 'Office: time and pay',
    audience: 'office',
    summary: 'One crew day: where it was recorded, its lines, corrections, moving it to another job or date, and its history.',
    routes: ['/office/submissions'],
    body: `
This screen shows one crew day: the job, date, crew and every pay line. Open it from **Open** on [Review by week](/office/review), or from **Open crew day** on Problems.

## The header

The heading shows the job and date, then the job name, crew count, lines, hours and who entered it. **Approved on entry** means the supervisor who recorded the day also approved it. There is no separate approval step.

Below that you may see where the day was recorded from:

- **recorded on site at** a time, with how accurate the reading was and how far it was from the job's pin, then a verdict such as **Away from site**.
- If the job has no pinned location, the reading cannot be placed. Use **pin it** to set one on Job locations.
- **No location recorded** with the supervisor's reason, if they did not tap in.

If the day has posted to Sage, a yellow note says so and the screen is read-only.

## Shift and work performed

If the supervisor entered start and end times or a description of the work, they show here. **Span** is the time between start and end. It is not the paid hours, which are entered separately and can differ. None of this goes to Sage.

## Correcting lines

1. Type a reason in **Reason for corrections**. It is optional for the office, but it is saved against each change.
2. In **Lines**, click **Correct** on a line. Change the **Work type** or the **Hours / amount** and click **Save**.
3. To take a line off, click **Remove** and confirm.

**Codes sent to Sage** shows the cost code and earnings code Sage will receive. They change together when you change the work type. **↳ charged to** under a work type means travel booked to the job the person drove to.

The office can correct a per diem amount here. Supervisors cannot, because the rate should come from the rate sheet.

## Wrong job or date?

Use this when the whole day was recorded on the wrong job or the wrong date.

1. Fill in the reason above first; the move uses it.
2. Click **Move this day**. Pick the new **Job** and/or **Date**.
3. Click **Check the move**. Nothing changes yet. You see either **Nothing to flag. Ready to move.** or warnings, for example someone already recorded elsewhere on the new date.
4. Click **Move to** *job* **on** *date* to do it.

Every line, the per diem and the site reading move together. Per diem is re-priced for the new job's location. Work types are **not** changed: if the day moved between a prevailing wage job and a normal one, it appears on Problems under **Crew days moved onto a different kind of job** until you correct the work types here.

A move is refused when any line has already gone to Sage, when either date is in a week Sage has paid, or when per diem would land on a day that already has it.

## Correction history

Every change, with **When**, **Who**, **Crew member**, **Work type** (the line it was made on, such as Regular, Travel or Per diem), **Change** (old value → new value) and **Reason**. Two rows for one person at the same moment are two different lines, for example the hours and the per diem.

To read every change across a whole week, use [Changes by week](/help/changes-by-week).
`,
  },

  // ------------------------------------------------------------------ //
  {
    slug: 'per-diem-register',
    title: 'Per diem register',
    section: 'Office: time and pay',
    audience: 'office',
    summary: 'All recorded per diem in the workbook layout, by crew or job, with days removed in bulk.',
    routes: ['/office/per-diem-register'],
    body: `
The per diem register shows what per diem was recorded, laid out like the per diem workbook: a block per crew and job, one row per person, one column per day, and a total on the right. Seeing a whole crew on one block makes the usual problem obvious: one person claimed seven days while everyone beside them claimed two.

Per diem is run as its own payroll and sits outside the hours approval. Eligibility is payroll's call.

## Choosing what to show

- **Show** — **A week** or **A month**. A month is wider and scrolls sideways.
- **Week ending** or **Month** — the period. Paid weeks are marked **— paid**.
- **Group by** — **Crew** (blocks by supervisor) or **Job** (blocks by job, with a **Crew** column).
- **Crew** and **Job** — narrow to one. **Clear filters** resets them.

The summary line shows the period, total dollars, days and people.

People are filed under their own crew, not under whoever keyed the line, so a day the office entered for a supervisor still shows in that supervisor's block.

## Columns

| Column | What it means |
|---|---|
| **State of res.** | Home state and town, with any note about why they qualify. A dash means nothing recorded yet; set it on the Employees screen |
| **Classification** | Per diem tier: Super, QC or Technician. **no tier** means the person needs one before they can be priced |
| **Rate** | Daily rate. **mixed** means more than one rate in the period |
| Day columns | The amount for that day, or a dot for none |
| **Days** / **Total** | The person's count and dollars for the period |

A site name badge in the block heading means the job uses a named site rate instead of its state's rate.

## Removing days

1. Tick the days that should not have been claimed. Clicking a person's name ticks their whole row, which is quicker when you are cutting seven days to two: tick the row, then untick the two that stay.
2. The bar at the bottom shows how many days and how much will come off.
3. Add a **Reason (optional, kept on the audit trail)**.
4. Click **Remove**. A message confirms the days, people and dollars removed.

It is all or nothing: if any day can't be removed, nothing changes. Crew days that end up with no lines are kept, so the history still shows which job and day the per diem came off.

## Why are some days grey?

Greyed amounts are already paid or posted to Sage. They are shown so the period total is honest, but they can't be ticked. If part of a month is paid, a note names the paid weeks. If the whole period is paid, the screen is for reference only and any correction has to be made in Sage.

To **add** per diem, use **Per diem** on [Crew by week](/office/crew-week).
`,
  },

  // ------------------------------------------------------------------ //
  // TODO(verify): assumed a rate change only prices per diem recorded after
  // the change (amounts are stored on each line when recorded). Confirm.
  {
    slug: 'per-diem-rates',
    title: 'Per diem rates',
    section: 'Office: time and pay',
    audience: 'office',
    summary: 'The rate sheet: daily per diem by location and tier, and how a job picks its rate.',
    routes: ['/office/per-diem'],
    body: `
This is the per diem rate sheet. Each row is a location with three daily rates, one per tier: **Super**, **QC** and **Technician**.

## How a job finds its rate

- A job normally takes **its state's** rate.
- A job can instead name a site location in Sage (the per diem location field on the job). Rows marked **site** are named sites, such as Dutch Harbor or the Micron site, and are only ever used by a job that names them.
- The person's tier picks which of the three amounts applies.

If a job has no state, no rate can be found and per diem can't be entered on it. That shows on [Problems](/office/problems) as **Jobs with no state**. A site in a different state from its job shows as **Per diem site in the wrong state**.

Nobody types a per diem rate when recording a day. The portal always looks it up here, which is why this sheet matters.

## Changing a rate

1. Use **Filter by state or location** to find the row.
2. Click into the amount, type the new rate and click or tab away. It saves as soon as you leave the box, and a message confirms the new amount.

There is no Save button, so check the number before you leave the box.

## Adding a location

1. Click **+ Add location**.
2. Enter a **Location code**, the two-letter **State** and a **Description**.
3. Enter all three tier rates. All three are required: a location missing one would leave that tier quietly unpaid.
4. Click **Add location**.

A code different from its state (for example AK-DUTCH in AK) is treated as a named site, so only jobs pointed at it will use it.

## Why can't I rename a location or change its state?

The location, state and tier are what jobs look up. Changing them in place would silently repoint every job that uses that row, including work already done. Add a new location instead.
`,
  },

  // ------------------------------------------------------------------ //
  {
    slug: 'problems',
    title: 'Problems',
    section: 'Office: time and pay',
    audience: 'office',
    summary: 'Everything the portal can tell is set up wrong, what each check means, and how to clear it.',
    routes: ['/office/problems'],
    body: `
Problems lists everything the portal can tell is set up wrong, in one place. The number beside **Problems** in the menu is the count of open rows.

## How it works

- Every check runs fresh each time the screen loads. **Fix the underlying data and the row disappears.** There is nothing to mark as done.
- Click **Recheck** after fixing something to reload.
- Every check is listed, even with a count of zero. Zero means the check ran and found nothing.
- Click a category to open its rows: **What**, **Detail**, and buttons to go where it gets fixed.
- **This is fine** hides a row you have looked at and judged correct. You must give a reason, and it is saved against your name.
- **Include rows already dismissed** brings hidden rows back into view. **Restore** puts one back on the list.

## The checks

| Check | What it means | How to clear it |
|---|---|---|
| **Weeks that look like overtime, with none applied** | A person's week is over the overtime limits and has no overtime line. They will be paid short | Re-code hours as overtime in the [Labor register](/office/register). See [Applying overtime](/help/applying-overtime) |
| **Crew days moved onto a different kind of job** | A day moved between a prevailing wage job and a normal one still has the old job's work types, so the rate is wrong | **Open crew day** and correct the work types on each line (for example Regular to Prevailing Regular) |
| **Prevailing wage jobs with no rate** | Hours fall back to the employee card and are flagged unreliable | **Wage rates**: add the job's rates |
| **Per diem site in the wrong state** | The job points at a site rate in another state, so someone is being overpaid | **Open job** and correct the per diem location |
| **Jobs with no state** | No per diem rate can be found, so per diem can't be entered | **Open job**; the state is set in Sage |
| **Field staff on no crew** | Nobody can record their time | **Crews**: put them on a crew |
| **Field staff with no job title in Sage** | Job title is how supervisors are recognized before names are loaded | Fix in Sage; there is no portal button |
| **Portal writes Sage did not keep** | Someone had the job open in Job Maintenance and Sage saved over the portal's change | **Open job** and make the change again once the job is closed in Sage |

If you also have access to Certifications, certification checks (expired, missing, expiring soon and so on) appear here too, with **Open certifications** to fix them.

## Tips

- Work the high-priority checks first. Overtime and moved crew days affect this week's pay.
- Use **This is fine** sparingly. A dismissed row stays hidden even if the data gets worse later.
`,
  },

  // ------------------------------------------------------------------ //
  // TODO(verify): the export to Sage is not built yet. Rewrite this article
  // when it is, with the real button names and what happens on failure.
  {
    slug: 'sending-time-to-sage',
    title: 'Sending time to Sage',
    section: 'Office: time and pay',
    audience: 'office',
    summary: 'Where the portal stops and Sage takes over, and what locks once time reaches Sage or Sage pays a week.',
    body: `
Sage 100 stays the system of record for payroll. The portal collects and checks the field's time; Sage pays it.

## Where things stand today

**The portal does not yet send time to Sage on its own.** The automatic transfer is still being built and tested against a test company. Until it is turned on, nothing in the portal changes Sage payroll, and every line in the [Labor register](/office/register) shows **NotPosted**.

That is why the register mirrors Sage Time Track Entry column for column: you can read the two side by side.

## The office workflow, end to end

1. **Review.** Check each crew's week on [Crew by week](/office/crew-week) or each job's week on [Review by week](/office/review).
2. **Correct.** Fix hours, work types and missing lines in the [Labor register](/office/register) or on the crew day. Move days recorded on the wrong job or date. Every change is kept in the audit trail.
3. **Apply overtime.** Clear the overtime warning. See [Applying overtime](/help/applying-overtime).
4. **Settle per diem** on the [Per diem register](/office/per-diem-register).
5. **Final check.** Labor register, **Not yet sent to Sage** ticked, **By employee**.
6. **Pay in Sage.** Once the transfer is live, this is where the week's lines go to Sage.

## What locks, and when

There are two separate locks.

**When a line reaches Sage.** Each line's **Sage** status changes from **NotPosted** once it has been sent. From then on it shows **locked** in the register and cannot be corrected, removed or moved in the portal. If the whole crew day has posted, its screen says *This crew day has posted to Sage. Corrections must be made in Sage — the portal will not edit posted time.*

**When Sage pays the week.** As soon as Sage has payroll history for a week ending, the portal treats that week as paid, even though nothing was sent from the portal:

- [Crew by week](/office/crew-week) shows **Paid** with the check date and hides the **Per diem** button. Hours show **Sage has paid this period**.
- The [Per diem register](/office/per-diem-register) greys out paid days and won't remove them.
- A crew day can't be moved into or out of a paid week.

## Why the locks exist

Once Sage holds a line or has paid a week, changing the portal's copy would leave the two disagreeing, and the portal has no way to reverse a paycheck. A change that looks saved but never gets paid is worse than a refusal. So the portal refuses, and the correction is made in Sage.

> Make every correction **before** Sage pays the week. After that, fix it in Sage.
`,
  },
  // ------------------------------------------------------ /office/changes
  {
    slug: 'changes-by-week',
    title: 'Changes by week',
    section: 'Office: time and pay',
    audience: 'office',
    summary: 'Every correction to time in one pay week, newest first, with who made it and why.',
    routes: ['/office/changes'],
    body: `
**Time → Changes by week** gathers every change made to recorded time in one pay week (Sunday to Saturday) onto one screen: hours corrected, lines added or removed, per diem unticked, work types changed and days moved. Use it to read a week's changes before it goes to Sage, without opening each crew day.

Each crew day still has its own **Correction history**. This screen shows the same rows for the whole week.

## Reading it

1. Pick the week in **Week ending**. It opens on the current week.
2. Each row shows **When**, **Who** made the change (with an **Office** badge if it was the office), the **Job · day**, the **Crew member**, the **Work type** of the line, the **Change** (old → new) and the **Reason**.
3. Click **Crew day** to open that day.

The line under the filters counts the week's changes, and how many were made by supervisors and by the office.

## Filtering

- **Everyone / Supervisors / Office** shows only changes made from the field, or only the office's own.
- **Search** matches a person, job, work type or note.

## Good to know

- A day moved into this week shows here with its move. A day moved out shows in its new week.
- Supervisors must give a reason for every change. The office does not have to, so office rows may show a dash.
- Changes to start times and the work description are not listed; they do not change pay. Driver ticks are, because they change the travel code.
`,
  },
  // ------------------------------------------------- /office/send-to-sage
  {
    slug: 'send-to-sage',
    title: 'Send to Sage',
    section: 'Office: time and pay',
    audience: 'office',
    summary: 'Check a pay week line by line, then send it into Sage Time Track.',
    routes: ['/office/send-to-sage'],
    body: `
**Sage → Send to Sage** puts a pay week's time into Sage Time Track, so nobody has to key it in. Payroll then pulls it in with the Time Track button as usual, and labor posts to Job Cost through the Time Track register.

The portal never writes to Sage itself. A small service on the Sage server, the bridge (see [Bridge status](/help/sage-bridge)), writes each line through Sage's own rules, as if it had been keyed into Time Track Entry, and reports back here.

## Checking a week

1. Pick the **Sage company**. **TST** is the test company. **NWL** is live payroll and stays unavailable until the switch-over is approved.
2. Pick the **Pay week**.
3. Every line in the week is listed with exactly what Sage will get:
   - **Tax profile** and state: the person's own Sage profile for the job's state. A California job uses their California profile, not their usual Washington one.
   - **Workers' comp**: Sage's code for that state, or **None** on per diem.
   - **Pay rate**: the job's rate from [Wage rates](/help/wage-rates) on prevailing wage work, otherwise **Sage default** (the person's rate in Sage).
4. **Ready**, **Blocked**, **Sent** and **All** filter the list.

A **blocked** line says why, for example *"21-F1304 has no HI tax profile: add one in Employee Maintenance"*, *"Job N26112 is not in Sage company TST"* or *"Prevailing wage job with no rate for this classification and date"*. Fix the cause in Sage or in the portal, then pick the week again. Only that line waits; the rest can go.

## Sending

1. Untick any line you want to hold back. Every ready line starts ticked.
2. Optionally add a note.
3. Click **Send N lines to TST** (or NWL) and confirm.

The lines go into the bridge's queue. **Sent this week** follows them: **Waiting**, **Writing**, **In Sage** (with Sage's sequence number) or **Refused** (with Sage's message). The screen refreshes itself while anything is still on its way.

- **Cancel** takes a waiting line back out. Once the bridge has started on a line it can't be cancelled.
- **Retry** sends a refused line again unchanged, for example after the missing code has been added in Sage. If the line was corrected in the portal since, send it again from the week instead.
- A refused line that has already gone again is history, not a problem. It's folded away under **Show the lines replaced by batch N**, marked **Replaced**, and the batch it came from counts as Done once nothing else is outstanding. Look at batch N for how it went.
- The number on the **Sage** menu counts refused lines that haven't been sent again yet. It goes away once each one is retried or sent fresh.

## What sending changes

- **NWL (live):** a sent line is locked in the portal, showing *Already exported to Sage*. From then on it is corrected in Sage. A refused line unlocks again so it can be fixed here and re-sent.
- **TST (test):** nothing in the portal changes. Lines stay editable and can later go to NWL. After TST has been refreshed from NWL, an Admin can **Forget TST results for this week** to send the same week to TST again.

## Good to know

- A week Sage has already paid can't be sent.
- If the supervisor cutoff hasn't passed, a line warns that supervisors can still change the week.
- Nothing is ever sent twice: Sage's sequence number is recorded for every line, and a line in Sage is never offered again.
`,
  },
]
